import type { IOrder, ISamplePool } from '../../../../Model/index.js'
import type { ISampleMetaData } from '../../../../Model/Sample/ISampleMetaData.js'
import type { CSVMappingFlags, CSVMappingResult, CSVMode, SampleOperationResult } from './CSVModalTypes.js'
import { determineSampleOperations } from './DetermineSampleOperations.js'
import type { ImportAssessment, SheetStructurePlan } from './SheetStructurePlan.ts'
import { assessSheetStructure, planSheetStructure } from './SheetStructurePlan.ts'

export type SheetColumnsUsed = {
  /** Set when a pool column is used; `auto` false when it was picked by hand against the detector. */
  poolColumn?: { auto: boolean; reason: string }
  /** Whether the pool values may name pools. */
  useNames: boolean
}

export type CsvImportPlan = {
  ops: SampleOperationResult
  /** Present when the sheet states types or pools for an order whose items can still change. */
  structure?: SheetStructurePlan
  assessment?: ImportAssessment
}

export const planCsvImport = (
  pools: ISamplePool[],
  metaDatas: ISampleMetaData[],
  rows: CSVMappingResult[],
  flags: CSVMappingFlags,
  mode: CSVMode,
  order?: IOrder,
  sheet: SheetColumnsUsed = { useNames: false }
): CsvImportPlan => {
  const ops = determineSampleOperations(
    pools.flatMap((pool) => [...pool.samples, ...pool.negativeControls]),
    metaDatas,
    rows,
    flags,
    mode,
    order
  )
  /* types and pools only change while the lab can still edit the items, which is the create-samples mode */
  if (mode !== 'CREATE_SAMPLES' || ops.errors.length > 0 || !rows.some((row) => row.itemType || row.pool)) {
    return { ops }
  }
  const matched = ops.matched ?? []
  const structure = planSheetStructure(pools, matched, { useNames: sheet.useNames })
  const assessment = assessSheetStructure(pools, structure, {
    matchedItems: matched.filter((entry) => !entry.replica).length,
    poolColumn: sheet.poolColumn
  })
  return { ops, structure, assessment }
}
